// Where a sample / study is routed when staff choose outsourced vendor.
//
// Vendor options and rates come from GET
//   IPD: ipd_admission/{id}/billing/vendor-charge-capture/?medical_service_id=…
//   ER:  emergency/visits/{visit_id}/billing/vendor-charge-capture/?…
// (see api/vendorChargeCapture.js). Choosing a vendor posts the charge before
// the status move (lab Sample Sent / radiology Send to Outsourced).
import React, { useEffect, useMemo, useRef, useState } from "react";
import SvgIcon from "../ipd-billing/SvgIcon";
import { C } from "../ipd-billing/constants";
import { inputStyle } from "../rule-builder/ui";
import { validMedicalServiceId } from "./api/outsourcing";
import {
  fetchCommonVendorsForOrders,
  resolveBillingScope,
} from "./api/vendorChargeCapture";

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

export default function DispatchToVendorModal({
  group,
  patient,
  onClose,
  onConfirm,
  /** Lab offers in-house; radiology "Send to Outsourced" requires a vendor. */
  requireVendor = false,
  title = "Send sample",
  confirmInHouseLabel = "Send to in-house lab",
}) {
  const orders = useMemo(() => group?.orders || [], [group]);
  const billingScope = useMemo(() => resolveBillingScope(patient), [patient]);
  const scopeKey = billingScope
    ? billingScope.type === "ipd"
      ? `adm:${billingScope.admissionId}`
      : `er:${billingScope.visitId}`
    : "";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [testDetails, setTestDetails] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [dispatchRef, setDispatchRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [partialCoverage, setPartialCoverage] = useState(false);
  const backdropPressRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setPartialCoverage(false);

    if (!billingScope) {
      setVendors([]);
      setTestDetails([]);
      setLoading(false);
      return undefined;
    }

    fetchCommonVendorsForOrders(billingScope, orders)
      .then((result) => {
        if (cancelled) return;
        setVendors(result.vendors || []);
        setTestDetails(result.tests || []);
        setPartialCoverage(!!result.partialCoverage);
        // In-house is the normal route — the sample goes to the lab bench
        // exactly as it did before this card existed — so nothing is
        // pre-selected and a vendor has to be picked on purpose. Pre-selecting
        // the first vendor sent a plain Send down the outsourcing path, and
        // when its charge capture came back empty the status was never posted
        // at all, so the sample reached neither the vendor nor the lab.
        setVendorId("");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Could not load vendor options:", err);
        setLoadError(
          err?.message || "Could not load vendor options. Please try again.",
        );
        setVendors([]);
        setTestDetails([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey, orders, billingScope]);

  const selected =
    vendors.find((v) => String(v.vendor_id) === String(vendorId)) || null;
  const canConfirm =
    !submitting && !loading && (!requireVendor || !!selected);
  const noCommonVendor =
    !loading &&
    vendors.length === 0 &&
    testDetails.some((t) => t.vendors.length > 0);

  const submit = async () => {
    if (!canConfirm) return;
    if (requireVendor && !selected) return;
    setSubmitting(true);
    try {
      await onConfirm(
        selected ? { vendor: selected, dispatchRef } : { vendor: null },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const testMode = (detail) => {
    if (!detail) return "IN_HOUSE";
    if (detail.vendors.length === 0) return "IN_HOUSE";
    return "BOTH";
  };

  const rowStyle = (active) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1.5px solid ${active ? C.primary : C.border}`,
    background: active ? C.accent : "#fff",
    cursor: loading || submitting ? "default" : "pointer",
    marginBottom: 8,
    opacity: loading || submitting ? 0.7 : 1,
  });

  return (
    <>
      <div
        onMouseDown={(e) => {
          backdropPressRef.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          if (backdropPressRef.current && e.target === e.currentTarget && !submitting) {
            onClose();
          }
          backdropPressRef.current = false;
        }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.55)",
          zIndex: 2000,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, 94vw)",
          maxHeight: "88vh",
          zIndex: 2001,
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
              {title}
            </div>
            <div style={{ fontSize: 11.5, color: C.textSub, marginTop: 2 }}>
              {patient?.name || "—"}
              {group?.sample ? ` · ${group.sample}` : ""}
              {group?.barcode && group.barcode !== "-"
                ? ` · ${group.barcode}`
                : group?.orders?.[0]?.service
                  ? ` · ${group.orders[0].service}`
                  : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              border: "none",
              background: "none",
              cursor: submitting ? "default" : "pointer",
              padding: 4,
              display: "flex",
            }}
          >
            <SvgIcon name="x" size={16} color={C.textSub} />
          </button>
        </div>

        <div style={{ padding: "14px 18px", overflowY: "auto" }}>
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: C.textSub,
                marginBottom: 6,
              }}
            >
              {requireVendor ? "Studies" : "Tests on this sample"}
            </div>
            {orders.map((o) => {
              const detail = testDetails.find(
                (t) => t.order?.orderNo === o.orderNo,
              );
              const mode = testMode(detail);
              const hasService = validMedicalServiceId(o) != null;
              return (
                <div
                  key={o.orderNo}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    fontSize: 12,
                    color: C.text,
                    padding: "3px 0",
                  }}
                >
                  <span>{o.service}</span>
                  {!hasService ? (
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: "#FEF2F2",
                        color: "#B91C1C",
                        whiteSpace: "nowrap",
                      }}
                    >
                      No billing code
                    </span>
                  ) : mode !== "IN_HOUSE" ? (
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: "#F3F4F6",
                        color: C.textSub,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Can outsource
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>

          {loadError && (
            <div
              style={{
                fontSize: 12,
                color: C.danger,
                padding: "8px 0 12px",
              }}
            >
              {loadError}
            </div>
          )}

          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              color: C.textSub,
              marginBottom: 6,
            }}
          >
            Send to
          </div>

          {loading && (
            <div style={{ fontSize: 12, color: C.textSub, padding: "8px 0" }}>
              Loading vendor options…
            </div>
          )}

          {!loading && !requireVendor && (
            <div style={rowStyle(!vendorId)} onClick={() => !submitting && setVendorId("")}>
              <input type="radio" checked={!vendorId} readOnly />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                  In-house lab
                </div>
                <div style={{ fontSize: 11, color: C.textSub }}>
                  Normal bench flow — receive, process, report here
                </div>
              </div>
            </div>
          )}

          {!loading && requireVendor && vendors.length === 0 && !loadError && (
            <div style={{ fontSize: 12, color: C.textSub, padding: "8px 0" }}>
              No outsourced vendors are configured for these studies.
            </div>
          )}

          {noCommonVendor && (
            <div style={{ fontSize: 12, color: C.danger, padding: "8px 0" }}>
              No single vendor covers every outsourced test on this sample. Split
              the tests across separate samples, or send in-house.
            </div>
          )}

          {partialCoverage && vendors.length > 0 && (
            <div style={{ fontSize: 12, color: C.danger, padding: "8px 0" }}>
              Vendors listed may not run every outsourced test on this sample.
              Pick one that covers the tests you are sending, or split the
              sample.
            </div>
          )}

          {!loading &&
            vendors.map((v) => {
              const active = String(v.vendor_id) === String(vendorId);
              return (
                <div
                  key={v.vendor_id}
                  style={rowStyle(active)}
                  onClick={() =>
                    !submitting && setVendorId(String(v.vendor_id))
                  }
                >
                  <input type="radio" checked={active} readOnly />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 13,
                        fontWeight: 600,
                        color: C.text,
                      }}
                    >
                      {v.vendor_name}
                      {v.is_default && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "1px 5px",
                            borderRadius: 4,
                            background: "#EEF2FF",
                            color: "#4338CA",
                          }}
                        >
                          DEFAULT
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                      Cost {money(v.vendor_rate)}
                      {v.address ? ` · ${v.address}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}

          {selected && (
            <div style={{ marginTop: 12 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textSub,
                  marginBottom: 4,
                }}
              >
                Dispatch / courier reference (optional)
              </label>
              <input
                value={dispatchRef}
                onChange={(e) => setDispatchRef(e.target.value)}
                placeholder="e.g. BLR-4471-A"
                disabled={submitting}
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 18px",
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: "7px 14px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: submitting ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canConfirm}
            style={{
              padding: "7px 14px",
              borderRadius: 7,
              border: "none",
              background: canConfirm ? C.primary : C.border,
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              cursor: canConfirm ? "pointer" : "not-allowed",
              fontFamily: "inherit",
            }}
          >
            {submitting
              ? "Sending…"
              : selected
                ? `Dispatch to ${selected.vendor_name}`
                : confirmInHouseLabel}
          </button>
        </div>
      </div>
    </>
  );
}
